import axios from 'axios';
import { InventoryItem, InventoryTransaction } from '../types/inventory';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create dedicated axios instance for inventory service to avoid conflicts
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Add request interceptor to include auth token (using correct token key)
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token'); // Fixed: use 'auth_token' not 'token'
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/auth/login';
    }
    return Promise.reject(error);
  }
);

export const inventoryService = {
  // Dashboard Stats
  getDashboardStats: async () => {
    const response = await axiosInstance.get('/inventory/dashboard-stats');
    return response.data;
  },

  // Inventory Items

  getItems: async (params?: {
    search?: string;
    category?: string;
    stock_status?: string;
    page?: number;
    per_page?: number;
  }) => {
    const response = await axiosInstance.get('/inventory/items', { params });
    return response.data;
  },

  getItem: async (id: number) => {
    const response = await axiosInstance.get(`/inventory/items/${id}`);
    return response.data;
  },

  createItem: async (itemData: Partial<InventoryItem>) => {
    const response = await axiosInstance.post('/inventory/items', itemData);
    return response.data;
  },

  updateItem: async (id: number, itemData: Partial<InventoryItem>) => {
    const response = await axiosInstance.put(`/inventory/items/${id}`, itemData);
    return response.data;
  },

  deleteItem: async (id: number) => {
    const response = await axiosInstance.delete(`/inventory/items/${id}`);
    return response.data;
  },

  // Stock Management
  updateStock: async (id: string | number, stockData: {
    quantity: number;
    type: 'in' | 'out' | 'adjustment';
    reference_number?: string;
    notes?: string;
  }) => {
    const response = await axiosInstance.post(`/inventory/items/${id}/update-stock`, stockData);
    return response.data;
  },

  // Inventory Transactions
  getTransactions: async (params?: {
    item_id?: number;
    transaction_type?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
  }) => {
    const response = await axiosInstance.get('/inventory-transactions', { params });
    return response.data;
  },

  createTransaction: async (transactionData: Partial<InventoryTransaction>) => {
    const response = await axiosInstance.post('/inventory-transactions', transactionData);
    return response.data;
  },

  // Categories and Suppliers
  getCategories: async (params?: { search?: string }) => {
    const response = await axiosInstance.get('/inventory/categories', { params });
    return response.data;
  },

  createCategory: async (categoryData: { name: string; description?: string }) => {
    const response = await axiosInstance.post('/inventory/categories', categoryData);
    return response.data;
  },

  updateCategory: async (id: number, categoryData: { name: string; description?: string }) => {
    const response = await axiosInstance.put(`/inventory/categories/${id}`, categoryData);
    return response.data;
  },

  deleteCategory: async (id: number) => {
    const response = await axiosInstance.delete(`/inventory/categories/${id}`);
    return response.data;
  },

  getSuppliers: async (params?: { search?: string }) => {
    const response = await axiosInstance.get('/inventory/suppliers', { params });
    return response.data;
  },

  createSupplier: async (supplierData: { name: string; contact_person?: string; phone?: string; email?: string; address?: string }) => {
    const response = await axiosInstance.post('/inventory/suppliers', supplierData);
    return response.data;
  },

  updateSupplier: async (id: number, supplierData: { name: string; contact_person?: string; phone?: string; email?: string; address?: string }) => {
    const response = await axiosInstance.put(`/inventory/suppliers/${id}`, supplierData);
    return response.data;
  },

  deleteSupplier: async (id: number) => {
    const response = await axiosInstance.delete(`/inventory/suppliers/${id}`);
    return response.data;
  },

  // Low Stock and Near Expiry
  getLowStockItems: async () => {
    const response = await axiosInstance.get('/inventory/low-stock');
    return response.data;
  },

  getNearExpiryItems: async () => {
    const response = await axiosInstance.get('/inventory/near-expiry');
    return response.data;
  },

  // Recent Activities
  getRecentActivities: async () => {
    const response = await axiosInstance.get('/inventory/recent-activities');
    return response.data;
  },

  // Stock Report
  getStockReport: async (params?: { category_id?: number }) => {
    const response = await axiosInstance.get('/pharmacy/inventory/stock-report', { params });
    return response.data;
  },

  // Purchase Orders
  getPurchaseOrders: async (params?: {
    search?: string;
    status?: string;
    supplier_id?: number;
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
  }) => {
    const response = await axiosInstance.get('/pharmacy/orders', { params });
    return response.data;
  },

  getPurchaseOrder: async (id: number) => {
    const response = await axiosInstance.get(`/pharmacy/orders/${id}`);
    return response.data;
  },

  createPurchaseOrder: async (orderData: {
    supplier_id: number;
    order_date: string;
    expected_delivery_date?: string;
    items: Array<{
      inventory_item_id: number;
      quantity: number;
      unit_price: number;
      notes?: string;
    }>;
    notes?: string;
    terms_conditions?: string;
    tax_amount?: number;
    discount_amount?: number;
  }) => {
    const response = await axiosInstance.post('/pharmacy/orders', orderData);
    return response.data;
  },

  updatePurchaseOrder: async (id: number, orderData: {
    supplier_id: number;
    order_date: string;
    expected_delivery_date?: string;
    items: Array<{
      inventory_item_id: number;
      quantity: number;
      unit_price: number;
      notes?: string;
    }>;
    notes?: string;
    terms_conditions?: string;
    tax_amount?: number;
    discount_amount?: number;
  }) => {
    const response = await axiosInstance.put(`/pharmacy/orders/${id}`, orderData);
    return response.data;
  },

  updatePurchaseOrderStatus: async (id: number, status: string) => {
    const response = await axiosInstance.put(`/pharmacy/orders/${id}/status`, { status });
    return response.data;
  },

  receivePurchaseOrderItems: async (id: number, items: Array<{
    id: number;
    received_quantity: number;
  }>) => {
    const response = await axiosInstance.post(`/pharmacy/orders/${id}/receive`, { items });
    return response.data;
  },

  deletePurchaseOrder: async (id: number) => {
    const response = await axiosInstance.delete(`/pharmacy/orders/${id}`);
    return response.data;
  },

  getPurchaseOrderStatistics: async (params?: {
    date_from?: string;
    date_to?: string;
  }) => {
    const response = await axiosInstance.get('/pharmacy/orders/statistics', { params });
    return response.data;
  },

  // Inventory Requests
  createInventoryRequest: async (requestData: { employeeId?: number; items: { itemId: number; quantity: number }[]; notes?: string }) => {
    const response = await axiosInstance.post('/inventory-requests', requestData);
    return response.data;
  },
  
  getInventoryRequests: async (params?: { status?: string; employeeId?: number | string | undefined }) => {
    const response = await axiosInstance.get('/inventory-requests', { params });
    return response.data;
  },
  
  updateInventoryRequestStatus: async (id: number, status: string, notes?: string) => {
    const response = await axiosInstance.put(`/inventory-requests/${id}/status`, { status, notes });
    return response.data;
  },
};